import Editor from "@draft-js-plugins/editor"
import createMentionPlugin, { defaultSuggestionsFilter, MentionData } from "@draft-js-plugins/mention"
import "@draft-js-plugins/mention/lib/plugin.css"
import "@draft-js-plugins/emoji/lib/plugin.css"

import { CompositeDecorator, ContentState, convertFromRaw, convertToRaw, DraftHandleValue, EditorState } from "draft-js"
import "draft-js/dist/Draft.css"
import { clearEditorContent } from "draftjs-utils"
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { extractTextFromHtml } from "Shared/utils/htmlParserHelper"
import createLinkDetectionPlugin from "Shared/utils/plugins/draftjs/linkDetectionPlugin"
import createEmojiPlugin, { defaultTheme } from "@draft-js-plugins/emoji"

interface CommentEditorInterface {
    mentions: MentionData[]
    onSubmit: (newComment: string) => void
    defaultValue?: string
    isEditMode?: boolean
    onSetCurrentEditInput?: (currentComment: string) => any
}
const CommentEditor: React.FC<CommentEditorInterface> = ({ mentions, onSubmit, defaultValue, isEditMode, onSetCurrentEditInput }) => {
    const { t } = useTranslation()
    const ref = useRef<Editor>(null)

    // initiate mention plugin on editor
    const { MentionSuggestions, plugins, EmojiSuggestions, EmojiSelect } = useMemo(() => {
        const mentionPlugin = createMentionPlugin({
            positionSuggestions: (_settings: any) => {
                return {
                    transform: "scale(1) translateY(10%)",
                    transformOrigin: "1em 0% 0px",
                    transition: "all 0.25s cubic-bezier(0.3, 1.2, 0.2, 1)",
                }
            },
        })
        const { MentionSuggestions } = mentionPlugin
        const linkDetectionPlugin = createLinkDetectionPlugin()
        const emojiPlugin = createEmojiPlugin({
            theme: {
                ...defaultTheme,
                emoji: "emoji-custom-wrapper",
                emojiSelectButton: "custom-emoji-select-btn",
                emojiSelectButtonPressed: "custom-emoji-select-btn-pressed",
                emojiSelectPopover: "custom-emoji-select-popover",
                emojiSuggestionsEntryIcon: "custom-emoji-suggestions-entry-icon",
                emojiSelectPopoverNavEntryActive: "custom-emoji-select-popover-navEntryActive",
            },
            useNativeArt: true,
        })
        const { EmojiSuggestions, EmojiSelect } = emojiPlugin

        const plugins: any[] = [mentionPlugin, linkDetectionPlugin, emojiPlugin]
        return { plugins, MentionSuggestions, EmojiSuggestions, EmojiSelect }
    }, [])

    const [editorState, setEditorState] = useState<any>(() => EditorState.createEmpty())
    const [suggestions, setSuggestions] = useState<MentionData[]>([])
    const [open, setOpen] = useState(false)

    useEffect(() => {
        setSuggestions(mentions)

        // init editor with content in case in edit mode
        if (defaultValue) {
            let content
            // content can be editor config or plain text
            // we handle both in here
            try {
                content = convertFromRaw(JSON.parse(defaultValue) as any)
            } catch (error) {
                content = ContentState.createFromText(extractTextFromHtml(defaultValue || ""))
            }

            //inject decorators and stylings with content to preserve mentions
            const decorators = plugins.reduce((acc, p: any) => [...acc, ...p.decorators], [])
            setEditorState(EditorState.moveFocusToEnd(EditorState.createWithContent(content, new CompositeDecorator(decorators))))
        }
    }, [])

    const onSearchChange = useCallback(({ value }: { value: string }) => {
        const filtered = defaultSuggestionsFilter(value, mentions)
        if (filtered.length === 0) setOpen(false)
        setSuggestions(filtered.length > 0 ? filtered : ([] as MentionData[]))
    }, [])

    const getEditorContent = (content: any) => {
        const raw = convertToRaw(content)
        setEditorState(clearEditorContent(editorState))

        // important To avoid html parsing avatar link
        const rawObject = JSON.parse(JSON.stringify(raw))
        Object.keys(rawObject.entityMap).forEach((key) => delete rawObject.entityMap[key].data?.mention?.avatar)

        return JSON.stringify(rawObject)
    }

    const onChange = (es: EditorState) => {
        const content = editorState.getCurrentContent()
        if (content.hasText() && onSetCurrentEditInput) {
            onSetCurrentEditInput(getEditorContent(content))
        }

        setEditorState(es)
    }

    const handleReturnKey = (e: any, _edState: any): DraftHandleValue | any => {
        e.preventDefault()
        if (open) return

        const content = editorState.getCurrentContent()
        if (!content.hasText()) return "handled"

        onSubmit(getEditorContent(content))
        return "handled"
    }

    const onOpenChange = useCallback((openState: boolean) => {
        setOpen(openState)
    }, [])

    return (
        <div className="editor-mention" onClick={() => ref.current!.focus()}>
            <Editor
                editorKey={"editor"}
                ref={ref}
                editorState={editorState}
                onChange={onChange}
                plugins={plugins}
                placeholder={t("placeholder:comment")}
                handleReturn={handleReturnKey}
                stripPastedStyles
            />
            <MentionSuggestions open={suggestions.length > 0 && open} onOpenChange={onOpenChange} onSearchChange={onSearchChange} suggestions={suggestions} />
            <EmojiSuggestions onOpen={() => onOpenChange(true)} onClose={() => onOpenChange(false)} />
            <div className={`emoji-select-wrapper ${isEditMode && "editMode"}`}>
                <Suspense fallback={<></>}>
                    <EmojiSelect closeOnEmojiSelect />
                </Suspense>
            </div>
        </div>
    )
}

export default CommentEditor
